import React, {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';

import {usePreviewContext} from './PreviewContext';
import {APIClient} from '../utils/APIClient';
import {API_URL} from '../settings/local_settings';
import {LocalStorage} from '../utils/LocalStorage';

/**
 * Contexto del cliente ACTIVO del portal (multi-cliente).
 *
 * Generaliza el molde de PreviewContext: todo el portal deriva "qué cliente estoy viendo"
 * de acá. Fuentes, en orden:
 *   1. snapshot `logged_user_clients` escrito en el login (payload `clients[]`),
 *   2. refresh autoritativo `GET portal/me/clients/` al montar (hidrata sesiones logueadas
 *      antes del deploy multi-cliente y permisos cambiados con la sesión abierta),
 *   3. fallback legacy (`logged_user_client_id` escalar) — el seguro de la ventana de skew
 *      BE/FE: con backend viejo TODO se comporta como siempre.
 *
 * El activo se persiste keyed por usuario (`portal_active_client_<userId>`) en localStorage
 * (último usado entre sesiones) + sessionStorage (cada pestaña puede mirar un cliente
 * distinto). Deep-link: `?client=<id>` activa ese cliente y limpia el param.
 */

const PortalClientContext = createContext(null);

const noop = () => {};

const legacyClientFromScalars = () => {
    const clientId = LocalStorage.fetchUserClientId();
    if (!clientId) return null;
    return {
        id: parseInt(clientId, 10),
        name: null,
        logo_url: LocalStorage.fetchAgencyLogoUrl(),
        display_name: LocalStorage.fetchAgencyDisplayName(),
        brand_primary_color: LocalStorage.fetchBrandPrimaryColor(),
        brand_secondary_color: LocalStorage.fetchBrandSecondaryColor(),
        portal_theme: LocalStorage.fetchPortalTheme(),
        features: null,
        modules: null,
    };
};

const legacyFallbackValue = () => {
    const client = legacyClientFromScalars();
    return {
        isPreview: false,
        clients: client ? [client] : null,
        activeClientId: client ? client.id : null,
        activeClient: client,
        activeModules: null,
        activeBranding: client,
        isMultiClient: false,
        needsClientChoice: false,
        setActiveClient: noop,
        loading: false,
    };
};

export const PortalClientProvider = ({children}) => {
    const userId = LocalStorage.fetchUserId();

    const [clients, setClients] = useState(() => LocalStorage.fetchUserClients());
    const [activeClientId, setActiveClientId] = useState(() => {
        const persisted = LocalStorage.fetchActivePortalClient(LocalStorage.fetchUserId());
        return persisted ? parseInt(persisted, 10) : null;
    });
    const [loading, setLoading] = useState(true);
    const fetchedRef = useRef(false);

    // Refresh autoritativo, una sola vez por montaje del portal.
    useEffect(() => {
        if (fetchedRef.current) return;
        fetchedRef.current = true;
        new APIClient(`${API_URL}portal/me/clients/`).call('GET')
            .then((data) => {
                if (data && Array.isArray(data.clients) && data.clients.length > 0) {
                    setClients(data.clients);
                    LocalStorage.saveUserClients(data.clients);
                }
            })
            .catch(noop)
            .finally(() => setLoading(false));
    }, []);

    const effectiveClients = useMemo(() => {
        if (clients && clients.length > 0) return clients;
        const legacy = legacyClientFromScalars();
        return legacy ? [legacy] : null;
    }, [clients]);

    const setActiveClient = useCallback((clientId) => {
        const parsed = parseInt(clientId, 10);
        if (!parsed) return;
        LocalStorage.saveActivePortalClient(userId, parsed);
        setActiveClientId(parsed);
    }, [userId]);

    // Deep-link ?client=<id> (la agencia manda un link por mail): se procesa al montar el
    // portal — un deep-link llega por carga completa, no por navegación SPA — y se limpia
    // el param con replaceState (no rompe el back). Sin react-router a propósito: el v7 no
    // resuelve en Jest y arrastraría el mock virtual a todo consumidor del contexto.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const requested = parseInt(params.get('client'), 10);
        if (!requested || !effectiveClients) return;
        if (effectiveClients.some(client => client.id === requested)) {
            setActiveClient(requested);
        }
        params.delete('client');
        const query = params.toString();
        window.history.replaceState(null, '', window.location.pathname + (query ? `?${query}` : ''));
    }, [effectiveClients, setActiveClient]);

    const value = useMemo(() => {
        const list = effectiveClients;
        const isMultiClient = !!list && list.length > 1;

        let resolvedId = activeClientId;
        if (list && resolvedId && !list.some(client => client.id === resolvedId)) {
            // El persistido ya no es suyo (acceso revocado): se descarta.
            resolvedId = null;
        }
        if (list && !resolvedId && list.length === 1) {
            resolvedId = list[0].id;
        }

        const activeClient = (list && resolvedId)
            ? list.find(client => client.id === resolvedId) || null
            : null;

        return {
            isPreview: false,
            clients: list,
            activeClientId: resolvedId,
            activeClient,
            activeModules: activeClient ? (activeClient.modules || null) : null,
            activeBranding: activeClient,
            isMultiClient,
            // Multi sin activo válido → pantalla de bienvenida (primer login multi o revocado).
            needsClientChoice: isMultiClient && !resolvedId,
            setActiveClient,
            loading,
        };
    }, [effectiveClients, activeClientId, setActiveClient, loading]);

    return (
        <PortalClientContext.Provider value={value}>
            {children}
        </PortalClientContext.Provider>
    );
};

/**
 * Hook unificado del portal. Cascada: preview de agencia (manda siempre y es mono-cliente
 * por definición) → provider → fallback legacy (componente montado fuera del provider).
 */
export const usePortalClient = () => {
    const preview = usePreviewContext();
    const context = useContext(PortalClientContext);

    if (preview.isPreview) {
        return {
            isPreview: true,
            clients: null,
            activeClientId: preview.previewClientId,
            activeClient: null,
            activeModules: preview.previewModules,
            activeBranding: null,
            isMultiClient: false,
            needsClientChoice: false,
            setActiveClient: noop,
            loading: false,
        };
    }
    if (context) {
        return context;
    }
    return legacyFallbackValue();
};
